import React, { useMemo, useState } from "react";
import { Drawer } from "vaul";
import { Minus, Plus, Trash2, ShoppingBag, ChevronLeft, X } from "lucide-react";
import { toast } from "sonner";
import { SmartImage } from "./media";
import { brl, maskPhoneBR, waNumber } from "../../lib/format";
import { api, apiErr } from "../../lib/api";

export default function CartDrawer({ open, onOpenChange, items, restaurant, products, setQty, removeItem, addUpsell, onOrderPlaced }) {
  const [step, setStep] = useState("cart");
  const subtotal = items.reduce((s, i) => s + i.unit * i.qty, 0);

  const upsells = useMemo(() => {
    const inCart = new Set(items.map((i) => i.product.id));
    const ids = new Set();
    items.forEach((i) => { if (["carrinho", "both"].includes(i.product.upsell_location)) (i.product.upsell_product_ids || []).forEach((id) => ids.add(id)); });
    const byId = Object.fromEntries((products || []).map((p) => [p.id, p]));
    return [...ids].map((id) => byId[id]).filter((p) => p && !p.sold_out && p.available !== false && !inCart.has(p.id)).slice(0, 6);
  }, [items, products]);

  const fee = restaurant.delivery_enabled ? Number(restaurant.delivery_fee || 0) : 0;

  return (
    <Drawer.Root open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setStep("cart"); }} dismissible={false}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-[70] bg-black/70" />
        <Drawer.Content className="fixed bottom-0 left-0 right-0 z-[71] mx-auto max-w-md max-h-[92vh] rounded-t-3xl menu-bg menu-text border-t menu-border outline-none flex flex-col">
          <button onClick={() => { onOpenChange(false); setStep("cart"); }} data-testid="cart-close" className="absolute top-4 right-4 z-20 h-8 w-8 rounded-full menu-chip flex items-center justify-center menu-text"><X size={18} /></button>
          {step === "cart"
            ? <CartStep items={items} restaurant={restaurant} upsells={upsells} subtotal={subtotal} fee={fee} setQty={setQty} removeItem={removeItem} addUpsell={addUpsell} onNext={() => setStep("checkout")} />
            : <CheckoutStep items={items} restaurant={restaurant} subtotal={subtotal} fee={fee} onBack={() => setStep("cart")} onOrderPlaced={onOrderPlaced} />}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

function CartStep({ items, restaurant, upsells, subtotal, fee, setQty, removeItem, addUpsell, onNext }) {
  const total = subtotal + (fee || 0);
  return (
    <>
      <div className="p-5 pb-2 pt-6 flex items-center gap-2"><ShoppingBag size={20} className="hmmm-text" /><h2 className="font-head text-xl font-extrabold menu-text">Seu carrinho</h2></div>
      <div className="flex-1 overflow-y-auto no-scrollbar px-5">
        {items.length === 0 && <p className="text-center menu-faint py-16">Seu carrinho está vazio.</p>}
        <div className="space-y-3">
          {items.map((i) => (
            <div key={i.key} className="flex gap-3 p-3 rounded-2xl menu-surface border menu-border" data-testid={`cart-item-${i.product.id}`}>
              <SmartImage src={i.product.image} className="h-16 w-16 rounded-xl shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-head font-bold text-sm line-clamp-1 menu-text">{i.product.name}</p>
                {i.addons.map((a, k) => <p key={k} className="text-[11px] menu-muted">+ {a.name}</p>)}
                {i.note && <p className="text-[11px] menu-faint italic">Obs: {i.note}</p>}
                <div className="flex items-center justify-between mt-1.5">
                  <div className="flex items-center gap-2 rounded-full menu-chip px-1.5 py-1">
                    <button onClick={() => setQty(i.key, i.qty - 1)} data-testid={`cart-minus-${i.product.id}`} className="h-6 w-6 flex items-center justify-center menu-text"><Minus size={13} /></button>
                    <span className="w-4 text-center text-sm font-bold menu-text">{i.qty}</span>
                    <button onClick={() => setQty(i.key, i.qty + 1)} data-testid={`cart-plus-${i.product.id}`} className="h-6 w-6 flex items-center justify-center menu-text"><Plus size={13} /></button>
                  </div>
                  <span className="font-head font-bold price-final">{brl(i.unit * i.qty)}</span>
                </div>
              </div>
              <button onClick={() => removeItem(i.key)} data-testid={`cart-remove-${i.product.id}`} className="menu-faint hover:text-rose-500 self-start"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        {items.length > 0 && upsells.length > 0 && (
          <div className="mt-5">
            <h4 className="font-head font-bold text-sm mb-2 menu-text">Vai bem com seu pedido</h4>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
              {upsells.map((u) => (
                <button key={u.id} onClick={() => addUpsell(u)} data-testid={`cart-upsell-${u.id}`} className="shrink-0 w-28 rounded-xl menu-surface border menu-border overflow-hidden text-left">
                  <SmartImage src={u.image} className="h-16 w-full" />
                  <div className="p-2"><p className="text-[11px] font-semibold line-clamp-1 menu-text">{u.name}</p><p className="text-[11px] price-final font-bold">+ {brl(u.promo_price || u.price)}</p></div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {items.length > 0 && (
        <div className="p-5 border-t menu-border space-y-1.5">
          <Row label="Subtotal" value={brl(subtotal)} />
          {fee > 0 && <Row label="Taxa de entrega" value={brl(fee)} />}
          <Row label="Total" value={brl(total)} big />
          <button onClick={onNext} data-testid="cart-continue" className="mt-3 w-full rounded-full py-3.5 font-bold text-white hmmm-gradient hmmm-glow active:scale-[0.98] transition-transform">Finalizar pedido</button>
        </div>
      )}
    </>
  );
}

function CheckoutStep({ items, restaurant, subtotal, fee: baseFee, onBack, onOrderPlaced }) {
  const canDelivery = restaurant.delivery_enabled;
  const canPickup = restaurant.pickup_enabled;
  const [mode, setMode] = useState(canDelivery ? "entrega" : "retirada");
  const pm = restaurant.payment_methods || {};
  const payOptions = useMemo(() => [["pix", "Pix"], ["dinheiro", "Dinheiro"], ["cartao", "Cartão"]].filter(([k]) => pm[k] !== false), [restaurant.payment_methods]);
  const [f, setF] = useState({ name: "", phone: "", cep: "", street: "", number: "", neighborhood: "", complement: "", reference: "", payment: payOptions[0]?.[0] || "pix", troco: "", notes: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const fee = mode === "entrega" ? (baseFee || 0) : 0;
  const total = subtotal + fee;

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Informe seu nome");
    if (f.phone.replace(/\D/g, "").length < 10) return toast.error("Informe um WhatsApp válido");
    if (mode === "entrega" && (!f.street.trim() || !f.number.trim())) return toast.error("Preencha o endereço de entrega");

    // Authoritative gate: account active + store open + accepting orders.
    setSending(true);
    try {
      await api.post(`/public/${restaurant.slug}/validate-order`);
    } catch (e) {
      setSending(false);
      return toast.error(apiErr(e));
    }
    setSending(false);

    const L = [];
    L.push(`*Novo pedido — ${restaurant.name}*`, "");
    L.push(`*Cliente:* ${f.name}`);
    L.push(`*WhatsApp:* ${f.phone}`);
    L.push(`*Tipo:* ${mode === "entrega" ? "Entrega" : "Retirada"}`);
    if (mode === "entrega") {
      L.push(`*Endereço:* ${f.street}, ${f.number}${f.complement ? " - " + f.complement : ""}${f.neighborhood ? " - " + f.neighborhood : ""}${f.cep ? " - CEP " + f.cep : ""}`);
      if (f.reference) L.push(`*Referência:* ${f.reference}`);
    }
    const payLabel = { pix: "Pix", dinheiro: "Dinheiro", cartao: "Cartão na entrega" }[f.payment];
    L.push(`*Pagamento:* ${payLabel}`);
    if (f.payment === "dinheiro" && f.troco) L.push(`*Troco para:* ${f.troco}`);
    if (f.notes) L.push(`*Observações:* ${f.notes}`);
    L.push("", "*Itens:*");
    items.forEach((i) => {
      L.push(`• ${i.qty}x ${i.product.name} — ${brl(i.unit * i.qty)}`);
      i.addons.forEach((a) => L.push(`   + ${a.name}${a.price ? ` (${brl(a.price)})` : ""}`));
      if (i.note) L.push(`   Obs: ${i.note}`);
    });
    L.push("", `Subtotal: ${brl(subtotal)}`);
    if (fee) L.push(`Taxa de entrega: ${brl(fee)}`);
    L.push(`*Total: ${brl(total)}*`);
    window.open(`https://wa.me/${waNumber(restaurant.whatsapp)}?text=${encodeURIComponent(L.join("\n"))}`, "_blank");
    toast.success("Pedido preparado! Abrindo o WhatsApp…");
    onOrderPlaced?.();
  };

  return (
    <>
      <div className="p-5 pb-2 pt-6 flex items-center gap-2">
        <button onClick={onBack} data-testid="checkout-back" className="h-8 w-8 rounded-full menu-chip flex items-center justify-center menu-text"><ChevronLeft size={18} /></button>
        <h2 className="font-head text-xl font-extrabold menu-text">Finalizar pedido</h2>
      </div>
      <div className="flex-1 overflow-y-auto no-scrollbar px-5 space-y-3" data-testid="checkout-step">
        <In label="Nome*" value={f.name} onChange={(v) => set("name", v)} tid="ck-name" />
        <In label="WhatsApp*" value={f.phone} onChange={(v) => set("phone", maskPhoneBR(v))} tid="ck-phone" placeholder="(11) 99999-0000" />
        {(canDelivery || canPickup) && (
          <div>
            <label className="text-xs font-semibold menu-muted">Como deseja receber?</label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {canDelivery && <ModeBtn active={mode === "entrega"} onClick={() => setMode("entrega")} tid="ck-mode-entrega">Entrega</ModeBtn>}
              {canPickup && <ModeBtn active={mode === "retirada"} onClick={() => setMode("retirada")} tid="ck-mode-retirada">Retirada</ModeBtn>}
            </div>
          </div>
        )}
        {mode === "entrega" && (
          <div className="grid grid-cols-2 gap-2">
            <In label="CEP" value={f.cep} onChange={(v) => set("cep", v)} tid="ck-cep" />
            <In label="Bairro" value={f.neighborhood} onChange={(v) => set("neighborhood", v)} tid="ck-bairro" />
            <div className="col-span-2"><In label="Rua*" value={f.street} onChange={(v) => set("street", v)} tid="ck-street" /></div>
            <In label="Número*" value={f.number} onChange={(v) => set("number", v)} tid="ck-number" />
            <In label="Complemento" value={f.complement} onChange={(v) => set("complement", v)} tid="ck-complement" />
            <div className="col-span-2"><In label="Referência" value={f.reference} onChange={(v) => set("reference", v)} tid="ck-reference" /></div>
          </div>
        )}
        <div>
          <label className="text-xs font-semibold menu-muted">Pagamento</label>
          <div className="grid gap-2 mt-1.5" style={{ gridTemplateColumns: `repeat(${Math.max(payOptions.length, 1)}, minmax(0,1fr))` }}>
            {payOptions.map(([v, l]) => (
              <ModeBtn key={v} active={f.payment === v} onClick={() => set("payment", v)} tid={`ck-pay-${v}`}>{l}</ModeBtn>
            ))}
          </div>
        </div>
        {f.payment === "dinheiro" && <In label="Troco para quanto?" value={f.troco} onChange={(v) => set("troco", v)} tid="ck-troco" placeholder="Ex: R$ 50,00" />}
        <In label="Observações" value={f.notes} onChange={(v) => set("notes", v)} tid="ck-notes" placeholder="Opcional" />

        <div className="rounded-2xl menu-surface border menu-border p-3 text-sm space-y-1">
          {items.map((i) => <div key={i.key} className="flex justify-between menu-muted"><span className="truncate mr-2">{i.qty}x {i.product.name}</span><span>{brl(i.unit * i.qty)}</span></div>)}
          <div className="border-t menu-border pt-1 mt-1 flex justify-between menu-muted"><span>Subtotal</span><span>{brl(subtotal)}</span></div>
          {fee > 0 && <div className="flex justify-between menu-muted"><span>Taxa</span><span>{brl(fee)}</span></div>}
          <div className="flex justify-between font-head font-extrabold text-base"><span className="menu-text">Total</span><span className="price-final">{brl(total)}</span></div>
        </div>
      </div>
      <div className="p-5 border-t menu-border">
        <button onClick={submit} disabled={sending} data-testid="checkout-whatsapp" className="w-full rounded-full py-3.5 font-bold text-white active:scale-[0.98] transition-transform disabled:opacity-60" style={{ background: "#25D366" }}>{sending ? "Verificando…" : "Enviar pedido pelo WhatsApp"}</button>
      </div>
    </>
  );
}

function In({ label, value, onChange, tid, placeholder = "" }) {
  return (
    <div>
      <label className="text-xs font-semibold menu-muted">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={tid} placeholder={placeholder}
        className="w-full mt-1 rounded-xl menu-input border menu-border px-4 py-2.5 text-sm outline-none focus:border-[#FF5A5F] menu-text placeholder:text-[color:var(--m-faint)]" />
    </div>
  );
}
function ModeBtn({ children, active, onClick, tid }) {
  return <button onClick={onClick} data-testid={tid} className={`py-2.5 rounded-xl text-sm font-semibold ${active ? "hmmm-gradient text-white" : "menu-chip menu-muted"}`}>{children}</button>;
}
function Row({ label, value, big }) {
  return (
    <div className="flex items-center justify-between">
      <span className={big ? "font-head font-extrabold text-lg menu-text" : "text-sm menu-muted"}>{label}</span>
      <span className={big ? "font-head font-extrabold text-lg price-final" : "text-sm menu-text"}>{value}</span>
    </div>
  );
}
